import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TablePagination,
  IconButton,
  Box,
  Paper,
  Alert,
  Snackbar,
  Button,
  Chip,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import walletApi from '../../api/wallet';
import { LAYOUT } from '../../constants/layout';
import { getErrorMessage } from '../../utils/errors';
import { translateCategoryType, CATEGORY_TYPE_OPTIONS } from './categoryUtils';
import CategoryForm from './CategoryForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const FILTER_TYPE_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  ...CATEGORY_TYPE_OPTIONS,
];

const DEBOUNCE_DELAY = 400;

const CategoryList = () => {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [error, setError] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [filterType, setFilterType] = useState('');
  const [filterName, setFilterName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedName(filterName);
      setPage(0);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [filterName]);

  const fetchCategories = useCallback(async (currentPage, limit, categoryType, name) => {
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: currentPage + 1,
        limit,
        order: 'creationdate',
      });

      if (categoryType) {
        queryParams.set('category_type', categoryType);
      }

      if (name) {
        queryParams.set('name', name);
      }

      const url = `/category?${queryParams.toString()}`;
      const response = await walletApi.get(url);

      if (response.status === 200) {
        setCategories(response.data.Categories || []);
        setTotalItems(response.data.TotalItens || 0);
      } else {
        setCategories([]);
        setTotalItems(0);
        setError('Nenhuma categoria encontrada.');
        setErrorOpen(true);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao carregar as categorias.'));
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories(page, rowsPerPage, filterType, debouncedName);
  }, [page, rowsPerPage, filterType, debouncedName, fetchCategories]);

  const handleDeleteClick = (category) => {
    setDeleteTarget(category);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await walletApi.delete(`/category/${deleteTarget.ID}`);
      setDeleteTarget(null);
      fetchCategories(page, rowsPerPage, filterType, debouncedName);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao excluir a categoria.'));
      setErrorOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setDeleteTarget(null);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseError = () => setErrorOpen(false);

  const handleFormClose = () => {
    setCreateOpen(false);
    setSelectedCategory(null);
  };

  const handleFormSave = () => {
    fetchCategories(page, rowsPerPage, filterType, debouncedName);
  };

  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
    setPage(0);
  };

  const handleFilterNameChange = (event) => {
    setFilterName(event.target.value);
  };

  const handleClearFilters = () => {
    setFilterType('');
    setFilterName('');
    setDebouncedName('');
    setPage(0);
  };

  const hasActiveFilters = filterType || filterName;

  return (
    <Container maxWidth={false} disableGutters sx={LAYOUT.PAGE_SPACING}>
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Lista de Categorias
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Criar Categoria
        </Button>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          boxShadow: theme.shadows[1],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
          }}
        >
          <FilterListIcon sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            Filtros
          </Typography>
          {hasActiveFilters && (
            <Chip
              label="Limpar"
              size="small"
              onClick={handleClearFilters}
              onDelete={handleClearFilters}
              deleteIcon={<ClearIcon />}
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nome"
              value={filterName}
              onChange={handleFilterNameChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filterType}
                onChange={handleFilterTypeChange}
                label="Tipo"
              >
                {FILTER_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: theme.shadows[2],
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vincula Posicao</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length > 0 ? (
                categories.map((category) => (
                  <TableRow
                    key={category.ID}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>{category.Name}</TableCell>
                    <TableCell>
                      {translateCategoryType(category.CategoryType)}
                    </TableCell>
                    <TableCell>
                      {category.AllowsPositionLink ? (
                        <Chip label="Sim" size="small" color="success" />
                      ) : (
                        <Chip label="Nao" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton
                          onClick={() => setSelectedCategory(category)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          onClick={() => handleDeleteClick(category)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" align="center" sx={{ py: 4 }}>
                      {debouncedName || filterType
                        ? 'Nenhuma categoria encontrada com os filtros aplicados.'
                        : 'Nenhuma categoria encontrada.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por pagina:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      <CategoryForm
        open={createOpen || Boolean(selectedCategory)}
        category={selectedCategory}
        onClose={handleFormClose}
        onSave={handleFormSave}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        itemName={deleteTarget?.Name}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
      />
    </Container>
  );
};

export default CategoryList;
